import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["region", "district"]
  static values = { 
    addressNumber: String,
    districtUrl: { type: String, default: '/admin/regions' }
  }

  connect() {
    this.initializeExistingSelections()
  }

  // Stimulus action method for region changes
  regionChanged(event) {
    const regionSelect = event.target
    const regionId = regionSelect.value
    const addressNumber = regionSelect.dataset.addressNumber || this.addressNumberValue
    
    this.updateDistricts(regionId, addressNumber)
  }

  async updateDistricts(regionId, addressNumber) {
    if (!regionId) {
      this.clearDistricts(addressNumber)
      return
    }
    
    try {
      const response = await fetch(`${this.districtUrlValue}/${regionId}/districts.json`)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const districts = await response.json()
      this.populateDistricts(districts, addressNumber)
      
    } catch (error) {
      console.error('Failed to load districts:', error)
      this.handleDistrictLoadError(addressNumber)
    }
  }

  populateDistricts(districts, addressNumber) {
    const districtSelect = this.findDistrictSelect(addressNumber)
    if (!districtSelect) return
    
    // Store current value to restore if possible
    const currentValue = districtSelect.value
    
    // Clear and rebuild options
    districtSelect.innerHTML = '<option value="">Select a district</option>'
    
    districts.forEach(district => {
      const option = document.createElement('option')
      option.value = district.id
      option.textContent = district.name
      districtSelect.appendChild(option)
    })
    
    // Re-enable the select
    districtSelect.disabled = false
    
    // Restore previous value if it exists in new options
    if (currentValue && districtSelect.querySelector(`option[value="${currentValue}"]`)) {
      districtSelect.value = currentValue
    }
    
    // Dispatch change event in case other controllers need to react
    districtSelect.dispatchEvent(new Event('change', { bubbles: true }))
  }

  clearDistricts(addressNumber) {
    const districtSelect = this.findDistrictSelect(addressNumber)
    if (!districtSelect) return
    
    districtSelect.innerHTML = '<option value="">Select a district</option>'
    districtSelect.disabled = true
  }

  handleDistrictLoadError(addressNumber) {
    const districtSelect = this.findDistrictSelect(addressNumber)
    if (!districtSelect) return
    
    districtSelect.innerHTML = '<option value="">Failed to load districts</option>'
    districtSelect.disabled = true
  }

  findDistrictSelect(addressNumber) {
    // Try to find by data attribute first
    let districtSelect = this.element.querySelector(`.js-district-select[data-address-number="${addressNumber}"]`)
    
    // If not found and we have district targets, use the appropriate one
    if (!districtSelect && this.districtTargets.length > 0) {
      if (addressNumber && !isNaN(addressNumber)) {
        const index = parseInt(addressNumber)
        districtSelect = this.districtTargets[index] || this.districtTargets[0]
      } else {
        districtSelect = this.districtTargets[0]
      }
    }
    
    return districtSelect
  }

  initializeExistingSelections() {
    // Initialize districts for regions that already have values
    this.regionTargets.forEach(regionSelect => {
      if (regionSelect.value) {
        const addressNumber = regionSelect.dataset.addressNumber || this.addressNumberValue
        this.updateDistricts(regionSelect.value, addressNumber)
      }
    })
  }

  // Target callbacks for dynamic content
  regionTargetConnected(regionSelect) {
    // Initialize if it has a value
    if (regionSelect.value) {
      const addressNumber = regionSelect.dataset.addressNumber || this.addressNumberValue
      this.updateDistricts(regionSelect.value, addressNumber)
    }
  }

  // Public method to manually trigger region change (useful for integration)
  triggerRegionChange(regionSelect) {
    const event = new Event('change', { bubbles: true })
    regionSelect.dispatchEvent(event)
  }

  // Public method to set region and update districts
  setRegion(regionId, addressNumber = null) {
    const targetAddressNumber = addressNumber || this.addressNumberValue
    const regionSelect = this.element.querySelector(`.js-region-select[data-address-number="${targetAddressNumber}"]`) ||
                        this.regionTargets[0]
    
    if (regionSelect) {
      regionSelect.value = regionId
      this.updateDistricts(regionId, targetAddressNumber)
    }
  }

  // Public method to get current district value
  getCurrentDistrict(addressNumber = null) {
    const targetAddressNumber = addressNumber || this.addressNumberValue
    const districtSelect = this.findDistrictSelect(targetAddressNumber)
    return districtSelect ? districtSelect.value : null
  }

  // Public method to set district value
  setDistrict(districtId, addressNumber = null) {
    const targetAddressNumber = addressNumber || this.addressNumberValue
    const districtSelect = this.findDistrictSelect(targetAddressNumber)
    
    if (districtSelect) {
      districtSelect.value = districtId
      districtSelect.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }
}